import Vue from 'vue';
import Component from 'vue-class-component';

import CatalogReservationConfigHeader from 'components/Catalog/CatalogReservationConfigHeader';
import CatalogClientDataStep from 'components/Catalog/CatalogClientDataStep';
import CatalogReservationConfirmation from 'components/Catalog/CatalogReservationConfirmation/CatalogReservationConfirmation.vue';
import CatalogAutomaticReservationCompletedStep from 'components/Catalog/CatalogAutomaticReservationCompletedStep';
import CatalogSelectReservationLocationStep from 'components/Catalog/CatalogSelectReservationLocationStep/CatalogSelectReservationLocationStep.vue';

import CreateOnlineReservationMutation from 'assets/graphql/mutations/catalog/CreateOnlineReservation.graphql';

import { useBoxCatalogViewStore } from 'ecommerce/stores/boxCatalogView.store.js';

@Component({
  props: {
    activity: {
      type: Object,
      required: true,
    },
    expirationDate: {
      type: String,
      required: true,
      default: '',
    },
    code: {
      type: [String, Object],
      required: false,
      default: null,
    },
    codeId: {
      type: Number,
      required: false,
      default: null,
    },
    codeExpirationDate: {
      type: String,
      required: true,
      default: '',
    },
    tabs: {
      type: Array,
      required: true,
    },
    locations: {
      type: Array,
      required: true,
    },
    isReservationBuy: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    CatalogReservationConfigHeader,
    CatalogClientDataStep,
    CatalogReservationConfirmation,
    CatalogAutomaticReservationCompletedStep,
    CatalogSelectReservationLocationStep,
  },
  setup() {
    const catalogViewStore = useBoxCatalogViewStore();

    return {
      catalogViewStore,
    };
  },
})
export default class CatalogReservationConfigAutomaticComponent extends Vue {
  // Available steps are 'select-location-date', 'client-data' and 'reservation-completed'
  step = 'select-location-date';
  stepperData = {
    selectDateStep: null,
    clientDataStep: null,
  };
  creatingReservation = false;
  reservationConfig = null;
  reservationError = null;
  locationId = null;
  doNotWantReservation = false;

  created() {
    this.$emit('onStepChange', this.step);
  }

  isChildLoading(loading) {
    this.creatingReservation = loading;
  }

  get stepComponent() {
    const componentMapper = {
      'select-location-date': {
        props: {
          activityId: this.activity.id,
          code: this.code,
          codeId: this.codeId,
          codeExpirationDate: this.codeExpirationDate,
          locations: this.locations,
          isReservationBuy: this.isReservationBuy,
          showAddToCartButton: this.doNotWantReservation,
          activity: this.activity,
        },
        componentName: 'CatalogSelectReservationLocationStep',
      },
      'client-data': {
        props: {
          reservationConfig: this.reservationConfig,
          dateChosen: this.stepperData.selectDateStep,
          disabled: this.creatingReservation,
          reservationError: this.reservationError,
          isReservationBuy: this.isReservationBuy,
          activity: this.activity,
        },
        componentName: 'CatalogClientDataStep',
      },
      'confirmation-data': {
        props: {
          loading: this.creatingReservation,
          reservationError: this.reservationError,
          selectDateStep: this.stepperData.selectDateStep,
          clientDataStep: this.stepperData.clientDataStep,
          isReservationBuy: this.isReservationBuy,
          activity: this.activity,
        },
        componentName: 'CatalogReservationConfirmation',
      },
      'reservation-completed': {
        props: {
          selectDateStep: this.stepperData.selectDateStep,
          clientDataStep: this.stepperData.clientDataStep,
          activity: this.activity,
          reservationConfig: this.reservationConfig,
        },
        componentName: 'CatalogAutomaticReservationCompletedStep',
      },
    };
    return componentMapper[this.step];
  }

  get showSkipReservationCheckbox() {
    return (
      this.step === 'select-location-date' &&
      this.isReservationBuy &&
      this.activity.partneractivity?.can_purchase_a_reservation &&
      this.activity.partneractivity?.can_purchase_a_reservation ===
        'ACTIVE_OPTIONAL'
    );
  }

  nextStep() {
    this.reservationError = null;
    const stepper = {
      'select-location-date': 'client-data',
      'client-data': 'confirmation-data',
      'confirmation-data': 'reservation-completed',
    };
    this.step = stepper[this.step];
    this.$emit('onStepChange', this.step);
  }

  previusStep() {
    this.reservationError = null;
    const stepper = {
      'reservation-completed': 'confirmation-data',
      'confirmation-data': 'client-data',
      'client-data': 'select-location-date',
    };
    this.step = stepper[this.step];
    this.$emit('onStepChange', this.step);
  }

  // function to go to the next step receive reservation data
  selectDateCompleted(data) {
    this.locationId = data.location.id;
    this.stepperData.selectDateStep = data;
    this.nextStep();
  }

  // function to go to the next step receive client data
  selectClientDataCompleted(data) {
    this.stepperData.clientDataStep = data;
    this.nextStep();
  }

  setReservationConfig(reservationConfig) {
    /* Used to condition the manual reservation tab in the parent component */
    this.reservationConfig = reservationConfig;
    this.$emit('coverManager', !!reservationConfig.cover_manager_reservation);
  }

  addToCart(reservationData) {
    this.$emit('addToCart', reservationData);
  }

  // Function to create reservation, bigbox or covermanager
  createReservation() {
    this.reservationError = null;
    const { clientDataStep, selectDateStep } = this.stepperData;
    this.creatingReservation = true;
    const input = {
      email: clientDataStep.email,
      first_name: clientDataStep.firstName,
      last_name: clientDataStep.lastName,
      comments: clientDataStep.comments,
      phone: clientDataStep.phone,
      code: this.code,
      reservation_time: selectDateStep.appointment.time,
      reservation_date: selectDateStep.date.date,
      location: this.locationId,
      activity: this.activity.id,
      reservation_source: this.catalogViewStore.flags.alreadySearchedByDate
        ? 'Ecommerce Búsqueda con Disponibilidad'
        : 'Ecommerce Reserva desde Experiencia',
    };

    const reservationStatus = {
      loading: true,
      success: false,
    };
    this.$emit('reservationCreation', reservationStatus);

    this.$apollo
      .mutate({
        mutation: CreateOnlineReservationMutation,
        variables: {
          input,
        },
      })
      .then(response => {
        if (response.data.create_online_reservation.errors.length < 1) {
          this.nextStep();
          reservationStatus.success = true;
        } else {
          this.showError(
            response.data.create_online_reservation.errors[0].messages[0],
          );
        }
      })
      .catch(() => {
        this.showError('Ha ocurrido un problema inesperado.');
      })
      .finally(() => {
        this.creatingReservation = false;
        reservationStatus.loading = false;
      });
  }

  // Function to add activity to cart with reservation data
  addActivityToCartWithReservation() {
    const { clientDataStep, selectDateStep } = this.stepperData;
    const reservationData = {
      email: clientDataStep.email,
      first_name: clientDataStep.firstName,
      last_name: clientDataStep.lastName,
      phone: clientDataStep.phone,
      reservation_time: selectDateStep.appointment.time,
      reservation_date: selectDateStep.date.date,
      location: this.locationId,
      reservation_source: 'Ecommerce Compra con Reserva',
    };

    this.addToCart(reservationData);
  }

  handleReservationCreation() {
    if (this.isReservationBuy) this.addActivityToCartWithReservation();
    else this.createReservation();
  }

  closeModal() {
    this.$emit('reservationCreation', {
      loading: false,
      success: true,
    });
  }

  showError(error) {
    this.reservationError = error;
  }
}
